import { LookalikeRatio, type ValidationError } from '@innovationdepartment/proxima-sdk-axios';
import type { AudienceFilters, ProximaAdAccount } from 'types/audiences';
import type { AdAccount, MetaAdAccount } from 'types/stores/ad-account-store';
import type { CustomAudiencesApi } from './api/customAudiencesApi.types';

/* Wire types for the proxima-audiences API come from proxima-sdk-axios, which generates them from the service's api.yml.
   Where the service accepts or returns more than api.yml states, the adapter in `types/audiences` is used instead.
   The rest of this file is the feature's own types. */
export type {
  AudienceDefinitionGenderEnum,
  AudienceDefinitionPreviewCountResponse,
  AudienceDeleteDestinationOutcome,
  AudienceDeleteOutcome,
  AudienceDeletePreview,
  AudienceDeletePreviewDestination,
  AudienceDeletePreviewUntrackedLookalike,
  AudienceDeleteUntrackedLookalikeOutcome,
  AudienceDelivery,
  AudienceDeliveryOperation,
  AudienceDeliveryStatus,
  AudienceDestinationKind,
  AudienceDestinationStatus,
  AudienceSnapshot,
  AudienceSource,
  AudienceStatus,
  MetaAdSet,
  PreviewAudienceRequest,
  PreviewOutcome,
  SnapshotStatus,
  ValidationError,
  ValueMetric
} from '@innovationdepartment/proxima-sdk-axios';
export type {
  AdAccountCapacity,
  AdPlatform,
  AudienceDefinition,
  AudienceDefinitionPreviewResponse,
  AudienceDeleteResult,
  AudienceDestination,
  AudienceFilters,
  AudienceTarget,
  ClarifyPreviewResponse,
  CreateAudienceRequest,
  DeclinePreviewResponse,
  DefinitionPreviewResponse,
  DestinationPlatform,
  MetaReportedSize,
  Paginated,
  PreviewCountRequest,
  ProximaAdAccount,
  UpdateAudienceRequest
} from 'types/audiences';
export type { LookalikeRatio };

export const LOOKALIKE_RATIOS: readonly LookalikeRatio[] = Object.values(LookalikeRatio);

export type AudienceFilterKey = keyof AudienceFilters;

/** Error returned by every `CustomAudiencesApi` method; `status` is null for network failures. */
export type CustomAudiencesApiError = {
  status: number | null;
  message: string;
  validationErrors?: ValidationError[];
  /** The axios error code when no response arrived, e.g. `ERR_NETWORK` or `ECONNABORTED`. */
  code?: string;
};

export type ApiResult<T> =
  | { data: T; error?: undefined }
  | { data?: undefined; error: CustomAudiencesApiError };

/** Everything a `CustomAudiencesApi` call needs besides its own arguments, and the api itself. */
export type CustomAudiencesApiContext = {
  /** The live or mock api that store actions call, as `useCustomAudiencesApi` picks it. */
  api: CustomAudiencesApi;
  brandId: string;
  /** Resolves the bearer token; `refresh` skips the token cache. */
  getToken: (options?: { refresh?: boolean }) => Promise<string>;
  /** Aborts the call, and any retry it has not started yet, when it fires. */
  signal?: AbortSignal;
};

/**
 * Where a Deliver-to ad account comes from.
 *
 * - `brand`: an account the brand connected, from proxima-integrations through nova's ad account store.
 * - `proxima`: an account Proxima owns, from proxima-audiences `GET /brand/:brandId/ad-accounts`.
 */
export type AdAccountSource = 'brand' | 'proxima';

/** What proxima-integrations answers when asked to re-read a Meta account's custom audience terms. */
export type AdAccountTosResult = { acceptedTos: boolean };

/* Brand-owned shapes, the same shapes nova's ad account store holds */
export type BrandAdAccount = MetaAdAccount;
export type BrandTiktokAdAccount = AdAccount;
export type BrandAdAccounts = {
  metaAdAccountsList: BrandAdAccount[];
  tiktokAdAccountsList: BrandTiktokAdAccount[];
};

/** Both sources the Deliver-to panel picks from. */
export type AdAccountsResponse = BrandAdAccounts & {
  proximaAdAccountsList: ProximaAdAccount[];
};

/** Display status of a definition row, in precedence order. */
export type AudienceDisplayStatus =
  | 'Deleted'
  | 'Paused'
  | 'Failed'
  | 'Building'
  | 'Delivering'
  | 'Populating'
  | 'Live'
  | 'No destinations'
  /* A snapshot, destination, or delivery lookup failed, so the status cannot be derived */
  | 'Unknown';
